import { DateTime } from "luxon";
import { Plus, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "@somethings-ui/web";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGroups } from "@/lib/api/group";
import {
  UserRole,
  useAssignGroups,
  useClearUserCache,
  useDevCleanupUser,
  useMarkUserOnboarded,
  useUpdateUser,
  type User,
} from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useUpdateUser as useUpdateUserForm } from "@/lib/hooks/user/use-update-user";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { getPrimaryRole, isMentorIdentity } from "@/lib/utils/system-roles";
import { cn } from "@/lib/utils";
import { AccountSectionCard } from "./account-field";
import statusChevronIcon from "./status-chevron-icon.svg";
import {
  inTrainingOptionDisabled,
  recordAccountStatus,
  statusChangeConfirm,
  type RecordAccountStatus,
} from "./record-account-status";
import { RecordStatusBadge } from "./record-status-badge";
import { useRecordSaveTracker } from "./use-record-save-tracker";

function useRecordActivation(user: User) {
  const updateUser = useUpdateUser();
  const { track } = useTrackEvent();
  const [open, setOpen] = useState(false);

  return {
    open,
    isPending: updateUser.isPending,
    openConfirm: () => setOpen(true),
    closeConfirm: () => setOpen(false),
    confirm: () => {
      const next = !user.deactivated;
      void updateUser
        .mutateAsync({
          userId: user.id,
          params: { deactivated: next },
        })
        .then(() => {
          track(PostHogEvents.USER_RECORD_ACTIVATION_CHANGE, {
            new_status: next ? "Deactivated" : "Activated",
            user_name: getMenteeDisplayName(user),
          });
          setOpen(false);
        });
    },
  };
}

function RecordActivationDialog({
  user,
  open,
  onClose,
  onConfirm,
  loading,
}: {
  user: User;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <ConfirmActionDialog
      isOpen={open}
      onClose={onClose}
      onConfirm={onConfirm}
      title={user.deactivated ? "Reactivate user" : "Deactivate user"}
      description={
        user.deactivated
          ? "This user will be able to use the platform again."
          : "All active mentorships will be deactivated."
      }
      confirmText={user.deactivated ? "Reactivate" : "Deactivate"}
      confirmVariant={user.deactivated ? "default" : "destructive"}
      loading={loading}
    />
  );
}

type AssignableRole = typeof UserRole.ADMIN | typeof UserRole.MENTOR;

function isAssignableRole(value: string): value is AssignableRole {
  return value === UserRole.ADMIN || value === UserRole.MENTOR;
}

function OrgRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-[52px] grid-cols-[minmax(7rem,10.5rem)_1fr] items-center gap-6 border-b border-[#dfddd8] px-6 py-3.5 last:border-b-0">
      <p className="pt-0.5 font-body text-xs font-medium tracking-[0.2px] text-[#6e6b67]">
        {label}
      </p>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

interface OrganizationSectionProps {
  user: User;
}

export function OrganizationSection({ user }: OrganizationSectionProps) {
  const ability = useAbility();
  const canUpdate =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");
  const canUpdateGroups =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "groups");
  const { toast } = useToast();
  const trackSave = useRecordSaveTracker("record", getMenteeDisplayName(user));
  const { data: groups } = useGroups();
  const assignGroups = useAssignGroups();
  const { handleUpdate, fieldState } = useUpdateUserForm({
    userId: user.id,
    role:
      user.role === UserRole.ADMIN || user.role === UserRole.MENTOR
        ? user.role
        : undefined,
  });
  const [role, setRole] = useState<AssignableRole>(
    user.role === UserRole.ADMIN ? UserRole.ADMIN : UserRole.MENTOR,
  );
  const primary = getPrimaryRole(user.systemRoles);
  const roleLabel = formatRoleLabel(primary) ?? user.role;
  const joinDate = DateTime.fromISO(String(user.created_at));
  const assigned = user.assigned_groups ?? [];
  const availableGroups =
    groups?.filter(
      (group) => !assigned.some((assignedGroup) => assignedGroup.id === group.id),
    ) ?? [];
  const canEditRole = canUpdate && isMentorIdentity(user.systemRoles);
  const isMentor = isMentorIdentity(user.systemRoles);
  const accountStatus = recordAccountStatus(user);
  const updateUser = useUpdateUser();
  const markOnboarded = useMarkUserOnboarded();
  const { track } = useTrackEvent();
  const [pendingStatus, setPendingStatus] = useState<RecordAccountStatus | null>(
    null,
  );
  const statusConfirm = pendingStatus
    ? statusChangeConfirm({
        to: pendingStatus,
        hasOnboarded: user.has_onboarded === true,
        isMentor,
      })
    : null;

  const applyStatus = () => {
    if (!pendingStatus || pendingStatus === accountStatus) {
      setPendingStatus(null);
      return;
    }

    const finish = () => {
      track(PostHogEvents.USER_RECORD_ACTIVATION_CHANGE, {
        new_status: pendingStatus === "deactivated" ? "Deactivated" : "Activated",
        user_name: getMenteeDisplayName(user),
      });
      setPendingStatus(null);
    };

    const fail = () => {
      toast({
        title: "Failed to update status",
        description: "Please try again.",
        variant: "destructive",
      });
    };

    if (pendingStatus === "deactivated") {
      void updateUser
        .mutateAsync({ userId: user.id, params: { deactivated: true } })
        .then(finish)
        .catch(fail);
      return;
    }

    if (pendingStatus === "in_training") {
      void updateUser
        .mutateAsync({ userId: user.id, params: { deactivated: false } })
        .then(finish)
        .catch(fail);
      return;
    }

    const activate = user.deactivated
      ? updateUser.mutateAsync({
          userId: user.id,
          params: { deactivated: false },
        })
      : Promise.resolve();
    void activate
      .then(() =>
        isMentor && user.has_onboarded !== true
          ? markOnboarded.mutateAsync({ userId: user.id })
          : undefined,
      )
      .then(finish)
      .catch(fail);
  };

  const persistGroups = (groupIds: string[]) => {
    void assignGroups
      .mutateAsync({ userId: user.id, groupIds })
      .then(() => trackSave("groups"));
  };

  return (
    <div>
      <h2 className="font-heading text-base font-bold leading-6 text-text-primary">
        Organization
      </h2>
      <AccountSectionCard className="mt-6">
        <OrgRow label="Role">
          {canEditRole ? (
            <Select
              value={role}
              onValueChange={(value) => {
                if (!isAssignableRole(value)) return;
                const previous = role;
                setRole(value);
                void handleUpdate("role", value).then((saved) => {
                  if (saved === false) {
                    setRole(previous);
                    toast({
                      title: "Failed to save changes",
                      variant: "destructive",
                    });
                    return;
                  }
                  if (saved) trackSave("role");
                });
              }}
              disabled={fieldState.role}
            >
              <SelectTrigger className="h-12 w-[200px] rounded-[17px] border-[#e2dccf]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UserRole.ADMIN}>Admin</SelectItem>
                <SelectItem value={UserRole.MENTOR}>Mentor</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="font-body text-[15px] leading-[22.5px] text-text-primary">
              {roleLabel}
            </p>
          )}
        </OrgRow>

        <OrgRow label="Groups">
          <div className="flex flex-wrap items-center gap-1.5">
            {assigned.map((group) => (
              <span
                key={group.id}
                className="inline-flex items-center gap-1 rounded-full border border-[rgba(31,30,29,0.08)] bg-[#f6f4ef] py-1 pl-2.5 pr-1"
              >
                <span className="font-body text-[13px] font-medium leading-[22.5px] text-text-primary">
                  {group.name}
                </span>
                {canUpdateGroups ? (
                  <button
                    type="button"
                    aria-label={`Remove ${group.name}`}
                    className="rounded-full p-0.5 text-[#58534b]"
                    onClick={() =>
                      persistGroups(
                        assigned
                          .filter((item) => item.id !== group.id)
                          .map((item) => item.id),
                      )
                    }
                  >
                    <X className="size-3.5" />
                  </button>
                ) : null}
              </span>
            ))}
            {canUpdateGroups && availableGroups.length > 0 ? (
              <Select
                value=""
                onValueChange={(groupId) =>
                  persistGroups([...assigned.map((group) => group.id), groupId])
                }
              >
                <SelectTrigger className="h-[33px] w-auto gap-1 rounded-full border-dashed border-[rgba(31,30,29,0.08)] px-2.5 shadow-none [&>svg]:hidden">
                  <Plus className="size-3.5 text-[#58534b]" />
                  <span className="font-body text-[13px] font-medium text-[#58534b]">
                    Add
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {availableGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>
        </OrgRow>

        <OrgRow label="Join date">
          <p className="font-body text-[15px] leading-[22.5px] text-text-primary">
            {joinDate.isValid ? joinDate.toFormat("MMMM d, yyyy") : "—"}
          </p>
        </OrgRow>

        <OrgRow label="Status">
          {canUpdate ? (
            <Select
              value={accountStatus}
              onValueChange={(value) => {
                if (
                  value !== "active" &&
                  value !== "deactivated" &&
                  value !== "in_training"
                ) {
                  return;
                }
                if (value === accountStatus) return;
                setPendingStatus(value);
              }}
              disabled={updateUser.isPending || markOnboarded.isPending}
            >
              <SelectTrigger className="h-auto w-fit justify-start gap-1 rounded-full border-0 bg-transparent p-0 shadow-none focus:ring-0 focus:ring-offset-0 [&>span]:overflow-visible [&>span]:line-clamp-none [&>svg]:hidden">
                <RecordStatusBadge status={accountStatus} />
                <img src={statusChevronIcon} alt="" width={14} height={14} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="deactivated">Deactivated</SelectItem>
                {isMentor ? (
                  <SelectItem
                    value="in_training"
                    disabled={inTrainingOptionDisabled(user)}
                  >
                    In Training
                  </SelectItem>
                ) : null}
              </SelectContent>
            </Select>
          ) : (
            <RecordStatusBadge status={accountStatus} />
          )}
        </OrgRow>
      </AccountSectionCard>

      {statusConfirm ? (
        <ConfirmActionDialog
          isOpen={pendingStatus !== null}
          onClose={() => setPendingStatus(null)}
          onConfirm={applyStatus}
          title={statusConfirm.title}
          description={statusConfirm.description}
          confirmText={statusConfirm.confirmText}
          confirmVariant={statusConfirm.destructive ? "destructive" : "default"}
          loading={updateUser.isPending || markOnboarded.isPending}
        />
      ) : null}
    </div>
  );
}

export function RecordDeactivateBar({ user }: { user: User }) {
  const ability = useAbility();
  const canUpdate =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");
  const canResetUser =
    import.meta.env.VITE_STYTCH_PUBLIC_ENV !== "production" &&
    ability.can("manage", "all");
  const activation = useRecordActivation(user);
  const clearCache = useClearUserCache();
  const resetUser = useDevCleanupUser();
  const [resetOpen, setResetOpen] = useState(false);
  const name = getMenteeDisplayName(user);
  const active = !user.deactivated;
  const statusLabel = {
    active: "active",
    deactivated: "deactivated",
    in_training: "in training",
  }[recordAccountStatus(user)];

  if (!canUpdate && !canResetUser) {
    return null;
  }

  return (
    <>
      <div className="mt-8 flex items-center justify-between rounded-2xl border border-[rgba(31,30,29,0.08)] px-6 py-3.5">
        <p className="font-body text-xs font-medium leading-4 text-[#58534b]">
          {name} is {statusLabel}
        </p>
        <div className="flex items-center gap-3">
          {canUpdate ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                fullWidth={false}
                className="min-w-[120px]"
                loading={clearCache.isPending}
                onPress={() => clearCache.mutate(user.id)}
              >
                Clear Cache
              </Button>
              <Button
                size="sm"
                fullWidth={false}
                className={cn(
                  "min-w-[120px]",
                  active ? "bg-states-error text-white" : undefined,
                )}
                loading={activation.isPending}
                onPress={activation.openConfirm}
              >
                {active ? "Deactivate" : "Reactivate"}
              </Button>
            </>
          ) : null}
          {canResetUser ? (
            <Button
              variant="destructive"
              size="sm"
              fullWidth={false}
              className="min-w-[120px]"
              loading={resetUser.isPending}
              onPress={() => setResetOpen(true)}
            >
              Reset User
            </Button>
          ) : null}
        </div>
      </div>
      <RecordActivationDialog
        user={user}
        open={activation.open}
        onClose={activation.closeConfirm}
        onConfirm={activation.confirm}
        loading={activation.isPending}
      />
      <ConfirmActionDialog
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={() => {
          resetUser.mutate(user.id, {
            onSuccess: () => setResetOpen(false),
          });
        }}
        title="Reset User Data"
        description="This will delete all user data including mentorships, messages, profile information, and mentor matching-profile reviews. This action cannot be undone."
        confirmText="Reset User"
        confirmVariant="destructive"
        loading={resetUser.isPending}
      />
    </>
  );
}

